import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Dashboard from './Dashboard';
import './VendorPaymentCenter.css';

const apiUrl = process.env.REACT_APP_API_URL || 'https://vakra.co.in/api';

const VendorPaymentCenter = () => {
  const token = localStorage.getItem('token');

  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('overview');

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [accountStatus, setAccountStatus] = useState({
    gateway_name: 'razorpay',
    vendor_type: '',
    connected: false,
    razorpay_linked_account_id: '',
    razorpay_product_id: '',
    razorpay_stakeholder_id: '',
    onboarding_status: 'not_started',
    kyc_status: 'pending',
    bank_verification_status: 'pending',
    dashboard_access_enabled: 0,
    settlement_hold: 0,
    is_active: 0,
    last_onboarding_sync_at: '',
    last_error_reason: '',
  });

  const [summary, setSummary] = useState({
    total_payments: 0,
    successful_payments: 0,
    failed_payments: 0,
    pending_payments: 0,
    refunded_payments: 0,
    today_collection: 0,
    pending_settlement_amount: 0,
    total_settled_amount: 0,
  });

  const [payments, setPayments] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [orphanPayments, setOrphanPayments] = useState([]);
  const [refundReport, setRefundReport] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedSettlement, setSelectedSettlement] = useState(null);

  const [paymentFilters, setPaymentFilters] = useState({
    search: '',
    status: '',
    dateRange: 'this_month',
  });

  const [settlementFilters, setSettlementFilters] = useState({
    search: '',
    status: '',
  });

  const axiosConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }),
    [token]
  );

  const safeArray = (val) => (Array.isArray(val) ? val : []);
  const text = (val) => String(val || '').toLowerCase().trim();

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const getErrorMessage = (err, fallback = 'Something went wrong.') =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  const getBadgeClass = (status) => {
    const s = text(status);
    if (['active', 'verified', 'success', 'paid', 'processed', 'settled', 'connected'].includes(s)) {
      return 'vpc-badge success';
    }
    if (['pending', 'created', 'processing', 'under_review'].includes(s)) {
      return 'vpc-badge warning';
    }
    if (['failed', 'inactive', 'error', 'needs_clarification', 'clarification_required', 'rejected'].includes(s)) {
      return 'vpc-badge danger';
    }
    return 'vpc-badge neutral';
  };

  const Badge = ({ children, status }) => (
    <span className={getBadgeClass(status || children)}>
      {children}
    </span>
  );

  const fetchStatus = async () => {
    const res = await axios.get(`${apiUrl}/vendor-onboarding/status`, axiosConfig);
    const row = res?.data?.row || res?.data?.account || res?.data?.data || res?.data || {};

    setAccountStatus({
      gateway_name: row.gateway_name || 'razorpay',
      vendor_type: row.vendor_type || '',
      connected: !!(row.razorpay_linked_account_id || row.linked_account_id),
      razorpay_linked_account_id: row.razorpay_linked_account_id || row.linked_account_id || '',
      razorpay_product_id: row.razorpay_product_id || row.product_id || '',
      razorpay_stakeholder_id: row.razorpay_stakeholder_id || row.stakeholder_id || '',
      onboarding_status: row.onboarding_status || 'not_started',
      kyc_status: row.kyc_status || 'pending',
      bank_verification_status: row.bank_verification_status || 'pending',
      dashboard_access_enabled: Number(row.dashboard_access_enabled || 0),
      settlement_hold: Number(row.settlement_hold || 0),
      is_active: Number(row.is_active || 0),
      last_onboarding_sync_at: row.last_onboarding_sync_at || '',
      last_error_reason: row.last_error_reason || '',
    });
  };

  const fetchPaymentSummary = async () => {
    try {
      const res = await axios.get(`${apiUrl}/vendor/payments/summary`, axiosConfig);
      const data = res?.data?.summary || res?.data?.data || {};
      setSummary({
        total_payments: Number(data.total_payments || 0),
        successful_payments: Number(data.successful_payments || 0),
        failed_payments: Number(data.failed_payments || 0),
        pending_payments: Number(data.pending_payments || 0),
        refunded_payments: Number(data.refunded_payments || 0),
        today_collection: Number(data.today_collection || 0),
        pending_settlement_amount: Number(data.pending_settlement_amount || 0),
        total_settled_amount: Number(data.total_settled_amount || 0),
      });
    } catch {
      setSummary({
        total_payments: 0,
        successful_payments: 0,
        failed_payments: 0,
        pending_payments: 0,
        refunded_payments: 0,
        today_collection: 0,
        pending_settlement_amount: 0,
        total_settled_amount: 0,
      });
    }
  };

  const fetchPayments = async () => {
    try {
      const params = new URLSearchParams();
      if (paymentFilters.search) params.append('search', paymentFilters.search);
      if (paymentFilters.status) params.append('status', paymentFilters.status);
      if (paymentFilters.dateRange) params.append('dateRange', paymentFilters.dateRange);

      const res = await axios.get(`${apiUrl}/vendor/payments?${params.toString()}`, axiosConfig);
      const items = res?.data?.items || res?.data?.payments || res?.data?.data || [];
      setPayments(safeArray(items));
    } catch {
      setPayments([]);
    }
  };

  const fetchSettlements = async () => {
    try {
      const res = await axios.get(`${apiUrl}/settlements/all`, axiosConfig);
      const items = res?.data?.items || res?.data?.settlements || res?.data?.data || [];
      setSettlements(safeArray(items));
    } catch {
      setSettlements([]);
    }
  };

  const fetchOrphanPayments = async () => {
    try {
      const res = await axios.get(`${apiUrl}/settlements/orphan-payments`, axiosConfig);
      const items = res?.data?.items || res?.data?.data || [];
      setOrphanPayments(safeArray(items));
    } catch {
      setOrphanPayments([]);
    }
  };

  const fetchRefundReport = async () => {
    try {
      const res = await axios.get(`${apiUrl}/settlements/refund-report`, axiosConfig);
      const items = res?.data?.items || res?.data?.data || [];
      setRefundReport(safeArray(items));
    } catch {
      setRefundReport([]);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await Promise.all([
        fetchStatus(),
        fetchPaymentSummary(),
        fetchPayments(),
        fetchSettlements(),
        fetchOrphanPayments(),
        fetchRefundReport(),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load payment center.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Login token not found. Please login again.');
      return;
    }
    fetchAll();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchPayments();
  }, [paymentFilters, token]);

  const handleTriggerPayout = async (settlementId) => {
    try {
      setError('');
      setMessage('');
      await axios.post(`${apiUrl}/settlements/route-payout`, { settlementId }, axiosConfig);
      await Promise.all([fetchSettlements(), fetchPaymentSummary(), fetchPayments()]);
      setMessage('Payout triggered successfully.');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to trigger payout.'));
    }
  };

  const handleExpireStalePayments = async () => {
    try {
      setError('');
      setMessage('');
      const res = await axios.post(`${apiUrl}/settlements/expire-stale-payments`, {}, axiosConfig);
      setMessage(res?.data?.message || 'Stale created payments processed.');
      await Promise.all([fetchPayments(), fetchOrphanPayments(), fetchPaymentSummary()]);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to process stale payments.'));
    }
  };

  const isOnboardingDone =
    Number(accountStatus.is_active) === 1 || text(accountStatus.onboarding_status) === 'active';

  const alerts = useMemo(() => {
    const list = [];

    if (!accountStatus.razorpay_linked_account_id) {
      list.push({
        type: 'danger',
        title: 'Linked account missing',
        text: 'Create linked account first.',
      });
    }

    if (accountStatus.razorpay_linked_account_id && !accountStatus.razorpay_stakeholder_id) {
      list.push({
        type: 'warning',
        title: 'Stakeholder missing',
        text: 'Create stakeholder to continue Route onboarding.',
      });
    }

    if (accountStatus.razorpay_stakeholder_id && !accountStatus.razorpay_product_id) {
      list.push({
        type: 'warning',
        title: 'Product config missing',
        text: 'Initialize Route product to generate product ID.',
      });
    }

    if (!isOnboardingDone && text(accountStatus.kyc_status) !== 'verified') {
      list.push({
        type: 'warning',
        title: 'KYC pending',
        text: 'Complete KYC/bank flow and sync status.',
      });
    }

    if (!isOnboardingDone && text(accountStatus.bank_verification_status) !== 'verified') {
      list.push({
        type: 'warning',
        title: 'Bank verification pending',
        text: 'Complete bank verification and sync status.',
      });
    }

    if (accountStatus.last_error_reason) {
      list.push({
        type: 'danger',
        title: 'Last sync issue',
        text: accountStatus.last_error_reason,
      });
    }

    if (orphanPayments.length > 0) {
      list.push({
        type: 'danger',
        title: 'Orphan paid payments found',
        text: `${orphanPayments.length} paid payments are not linked with orders.`,
      });
    }

    return list;
  }, [accountStatus, orphanPayments, isOnboardingDone]);

  const filteredSettlements = useMemo(() => {
    return settlements.filter((item) => {
      const s = text(settlementFilters.search);
      const st = text(settlementFilters.status);

      const matchSearch =
        !s ||
        text(item.order_id).includes(s) ||
        text(item.payment_id).includes(s) ||
        text(item.transfer_id || item.razorpay_transfer_id).includes(s) ||
        text(item.transfer_status).includes(s) ||
        text(item.settlement_status).includes(s);

      const matchStatus =
        !st || text(item.transfer_status) === st || text(item.settlement_status) === st;

      return matchSearch && matchStatus;
    });
  }, [settlements, settlementFilters]);

  const accountCompletion = useMemo(() => {
    if (isOnboardingDone) return 100;

    let score = 0;
    if (accountStatus.razorpay_linked_account_id) score += 25;
    if (accountStatus.razorpay_stakeholder_id) score += 25;
    if (accountStatus.razorpay_product_id) score += 25;
    if (text(accountStatus.kyc_status) === 'verified') score += 15;
    if (text(accountStatus.bank_verification_status) === 'verified') score += 10;

    return Math.min(score, 95);
  }, [accountStatus, isOnboardingDone]);

  const renderOverviewTab = () => (
    <>
      <div className="vpc-summary-grid">
        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Account Status</span>
          <span className="vpc-summary-value">
            {accountStatus.connected ? 'Connected' : 'Not Connected'}
          </span>
          <Badge status={accountStatus.is_active ? 'active' : 'inactive'}>
            {accountStatus.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        <div className="vpc-summary-card">
          <span className="vpc-summary-label">KYC Status</span>
          <span className="vpc-summary-value">{accountStatus.kyc_status}</span>
          <Badge status={accountStatus.kyc_status}>{accountStatus.kyc_status}</Badge>
        </div>

        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Today's Collection</span>
          <span className="vpc-summary-value">{formatCurrency(summary.today_collection)}</span>
          <span className="vpc-muted">Successful collections today</span>
        </div>

        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Pending Settlement</span>
          <span className="vpc-summary-value">
            {formatCurrency(summary.pending_settlement_amount)}
          </span>
          <span className="vpc-muted">Awaiting payout / processing</span>
        </div>

        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Failed Payments</span>
          <span className="vpc-summary-value">{summary.failed_payments}</span>
          <span className="vpc-muted">Transactions failed</span>
        </div>

        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Last Sync</span>
          <span className="vpc-summary-value">
            {accountStatus.last_onboarding_sync_at
              ? formatDateTime(accountStatus.last_onboarding_sync_at)
              : 'Never'}
          </span>
          <span className="vpc-muted">Onboarding sync time</span>
        </div>
      </div>

      <div className="vpc-grid-2">
        <div className="vpc-card">
          <h2 className="vpc-card-title">Payment Account</h2>
          <div className="vpc-info-grid">
            <div className="vpc-info-item">
              <span className="vpc-label">Gateway</span>
              <span className="vpc-value">{accountStatus.gateway_name}</span>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">Vendor Type</span>
              <span className="vpc-value">{accountStatus.vendor_type || '-'}</span>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">Linked Account ID</span>
              <span className="vpc-value">{accountStatus.razorpay_linked_account_id || '-'}</span>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">Stakeholder ID</span>
              <span className="vpc-value">{accountStatus.razorpay_stakeholder_id || '-'}</span>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">Product ID</span>
              <span className="vpc-value">{accountStatus.razorpay_product_id || '-'}</span>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">Settlement Hold</span>
              <span className="vpc-value">{accountStatus.settlement_hold ? 'Yes' : 'No'}</span>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">Dashboard Access</span>
              <span className="vpc-value">
                {accountStatus.dashboard_access_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">Last Sync</span>
              <span className="vpc-value">{formatDateTime(accountStatus.last_onboarding_sync_at)}</span>
            </div>
          </div>
        </div>

        <div className="vpc-card">
          <h2 className="vpc-card-title">Onboarding Progress</h2>

          <div className="vpc-progress-track">
            <div className="vpc-progress-fill" style={{ width: `${accountCompletion}%` }} />
          </div>

          <div className="vpc-progress-text">{accountCompletion}% Complete</div>

          <div className="vpc-step-list">
            <div className="vpc-info-item">
              <span className="vpc-label">Linked Account</span>
              <Badge status={accountStatus.razorpay_linked_account_id ? 'success' : 'pending'}>
                {accountStatus.razorpay_linked_account_id ? 'Done' : 'Pending'}
              </Badge>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">Stakeholder</span>
              <Badge status={accountStatus.razorpay_stakeholder_id ? 'success' : 'pending'}>
                {accountStatus.razorpay_stakeholder_id ? 'Done' : 'Pending'}
              </Badge>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">Product Config</span>
              <Badge status={accountStatus.razorpay_product_id ? 'success' : 'pending'}>
                {accountStatus.razorpay_product_id ? 'Done' : 'Pending'}
              </Badge>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">KYC Verification</span>
              <Badge status={accountStatus.kyc_status}>{accountStatus.kyc_status}</Badge>
            </div>
            <div className="vpc-info-item">
              <span className="vpc-label">Bank Verification</span>
              <Badge status={accountStatus.bank_verification_status}>
                {accountStatus.bank_verification_status}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderPaymentsTab = () => (
    <div className="vpc-card">
      <h2 className="vpc-card-title">Payment History</h2>

      <div className="vpc-filter-row">
        <select
          className="vpc-input"
          value={paymentFilters.dateRange}
          onChange={(e) => setPaymentFilters((prev) => ({ ...prev, dateRange: e.target.value }))}
        >
          <option value="today">Today</option>
          <option value="this_week">This Week</option>
          <option value="this_month">This Month</option>
          <option value="all">All Time</option>
        </select>

        <select
          className="vpc-input"
          value={paymentFilters.status}
          onChange={(e) => setPaymentFilters((prev) => ({ ...prev, status: e.target.value }))}
        >
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="failed">Failed</option>
          <option value="created">Created</option>
          <option value="pending">Pending</option>
          <option value="refunded">Refunded</option>
        </select>

        <input
          className="vpc-input vpc-input-wide"
          type="text"
          placeholder="Search by order id / payment id / customer"
          value={paymentFilters.search}
          onChange={(e) => setPaymentFilters((prev) => ({ ...prev, search: e.target.value }))}
        />
      </div>

      <div className="vpc-summary-grid">
        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Total Payments</span>
          <span className="vpc-summary-value">{summary.total_payments}</span>
        </div>
        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Successful</span>
          <span className="vpc-summary-value">{summary.successful_payments}</span>
        </div>
        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Failed</span>
          <span className="vpc-summary-value">{summary.failed_payments}</span>
        </div>
        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Pending</span>
          <span className="vpc-summary-value">{summary.pending_payments}</span>
        </div>
        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Refunded</span>
          <span className="vpc-summary-value">{summary.refunded_payments}</span>
        </div>
      </div>

      <div className="vpc-table-wrap">
        <table className="vpc-table">
          <thead>
            <tr>
              <th>Date & Time</th>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Payment Status</th>
              <th>Razorpay Order ID</th>
              <th>Razorpay Payment ID</th>
              <th>Settlement Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr>
                <td colSpan="10">No payments found.</td>
              </tr>
            ) : (
              payments.map((item) => (
                <tr key={item.payment_id || item.id}>
                  <td>{formatDateTime(item.created_at)}</td>
                  <td>{item.order_id || '-'}</td>
                  <td>{item.customer_number || '-'}</td>
                  <td>{formatCurrency(item.amount)}</td>
                  <td>{item.payment_method || '-'}</td>
                  <td>
                    <Badge status={item.payment_status || item.status}>
                      {item.payment_status || item.status || '-'}
                    </Badge>
                  </td>
                  <td>{item.razorpay_order_id || '-'}</td>
                  <td>{item.razorpay_payment_id || '-'}</td>
                  <td>
                    <Badge status={item.settlement_status}>{item.settlement_status || '-'}</Badge>
                  </td>
                  <td>
                    <button
                      className="vpc-link-btn"
                      onClick={async () => {
                        try {
                          const res = await axios.get(
                            `${apiUrl}/vendor/payments/${item.payment_id || item.id}`,
                            axiosConfig
                          );
                          setSelectedPayment(res?.data?.item || item);
                        } catch {
                          setSelectedPayment(item);
                        }
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSettlementsTab = () => (
    <div className="vpc-card">
      <h2 className="vpc-card-title">Settlement History</h2>

      <div className="vpc-filter-row">
        <select
          className="vpc-input"
          value={settlementFilters.status}
          onChange={(e) => setSettlementFilters((prev) => ({ ...prev, status: e.target.value }))}
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="processed">Processed</option>
          <option value="settled">Settled</option>
          <option value="failed">Failed</option>
        </select>

        <input
          className="vpc-input vpc-input-wide"
          type="text"
          placeholder="Search by order id / payment id / transfer id"
          value={settlementFilters.search}
          onChange={(e) => setSettlementFilters((prev) => ({ ...prev, search: e.target.value }))}
        />
      </div>

      <div className="vpc-summary-grid">
        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Pending Settlement</span>
          <span className="vpc-summary-value">
            {formatCurrency(summary.pending_settlement_amount)}
          </span>
        </div>
        <div className="vpc-summary-card">
          <span className="vpc-summary-label">Total Settled</span>
          <span className="vpc-summary-value">{formatCurrency(summary.total_settled_amount)}</span>
        </div>
      </div>

      <div className="vpc-table-wrap">
        <table className="vpc-table">
          <thead>
            <tr>
              <th>Settlement Date</th>
              <th>Order ID</th>
              <th>Payment ID</th>
              <th>Gross</th>
              <th>Commission</th>
              <th>Net Vendor</th>
              <th>Transfer Status</th>
              <th>Settlement Status</th>
              <th>Transfer ID</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredSettlements.length === 0 ? (
              <tr>
                <td colSpan="10">No settlements found.</td>
              </tr>
            ) : (
              filteredSettlements.map((item) => (
                <tr key={item.id || item.settlement_id}>
                  <td>{formatDateTime(item.settled_at || item.created_at)}</td>
                  <td>{item.order_id || '-'}</td>
                  <td>{item.payment_id || '-'}</td>
                  <td>{formatCurrency(item.gross_amount)}</td>
                  <td>{formatCurrency(item.commission_amount)}</td>
                  <td>{formatCurrency(item.net_vendor_amount)}</td>
                  <td>
                    <Badge status={item.transfer_status}>{item.transfer_status || '-'}</Badge>
                  </td>
                  <td>
                    <Badge status={item.settlement_status}>{item.settlement_status || '-'}</Badge>
                  </td>
                  <td>{item.razorpay_transfer_id || '-'}</td>
                  <td>
                    <div className="vpc-inline-actions">
                      <button className="vpc-link-btn" onClick={() => setSelectedSettlement(item)}>
                        View
                      </button>
                      {text(item.settlement_status) === 'pending' && (
                        <button
                          className="vpc-link-btn"
                          onClick={() => handleTriggerPayout(item.id || item.settlement_id)}
                        >
                          Trigger Payout
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderIssuesTab = () => (
    <div className="vpc-grid-2">
      <div className="vpc-card">
        <h2 className="vpc-card-title">Issues & Alerts</h2>
        {alerts.length === 0 ? (
          <div className="vpc-muted">No active alerts.</div>
        ) : (
          alerts.map((item, index) => (
            <div
              key={index}
              className={`vpc-issue-box ${item.type === 'danger' ? 'danger' : 'warning'}`}
            >
              <div className="vpc-issue-title">{item.title}</div>
              <div className="vpc-issue-text">{item.text}</div>
            </div>
          ))
        )}
      </div>

      <div className="vpc-card">
        <div className="vpc-section-head">
          <div>
            <h2 className="vpc-card-title no-margin">Orphan Paid Payments</h2>
            <p className="vpc-subtitle">Paid payments not linked with orders.</p>
          </div>
          <button className="vpc-btn-secondary" onClick={handleExpireStalePayments}>
            Process Stale Payments
          </button>
        </div>

        <div className="vpc-table-wrap">
          <table className="vpc-table">
            <thead>
              <tr>
                <th>Payment ID</th>
                <th>Order ID</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Paid At</th>
              </tr>
            </thead>
            <tbody>
              {orphanPayments.length === 0 ? (
                <tr>
                  <td colSpan="5">No orphan paid payments found.</td>
                </tr>
              ) : (
                orphanPayments.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.order_id || '-'}</td>
                    <td>{formatCurrency(item.amount)}</td>
                    <td>
                      <Badge status={item.status}>{item.status || '-'}</Badge>
                    </td>
                    <td>{formatDateTime(item.paid_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderReportsTab = () => (
    <div className="vpc-grid-2">
      <div className="vpc-card">
        <h2 className="vpc-card-title">Reports</h2>
        <div className="vpc-grid-2">
          <button className="vpc-report-btn">Download Payment Report CSV</button>
          <button className="vpc-report-btn">Download Settlement Report CSV</button>
          <button className="vpc-report-btn">Download Failed Payments CSV</button>
          <button className="vpc-report-btn">Download Refund Report CSV</button>
        </div>
      </div>

      <div className="vpc-card">
        <h2 className="vpc-card-title">Refund / Reversal Summary</h2>
        <div className="vpc-table-wrap">
          <table className="vpc-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Order ID</th>
                <th>Payment ID</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {refundReport.length === 0 ? (
                <tr>
                  <td colSpan="6">No refund report data found.</td>
                </tr>
              ) : (
                refundReport.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{formatDateTime(item.created_at || item.refunded_at)}</td>
                    <td>{item.order_id || '-'}</td>
                    <td>{item.payment_id || '-'}</td>
                    <td>{formatCurrency(item.amount)}</td>
                    <td>{item.type || item.refund_type || '-'}</td>
                    <td>
                      <Badge status={item.status}>{item.status || '-'}</Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (!token) {
    return (
      <div className="vpc-page">
        <div className="vpc-container">
          <div className="vpc-alert-danger">Login token not found. Please login again.</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Dashboard />
      <div className="vpc-page">
        <div className="vpc-container vpc-container-overflow-visible">
          <div className="vpc-header">
            <div>
              <h1 className="vpc-title">Payments & Settlement Center</h1>
              <p className="vpc-subtitle">
                Linked account, stakeholder, product config and sync status overview.
              </p>
            </div>

            <div className="vpc-action-row">
              <button className="vpc-btn-secondary" onClick={fetchAll} disabled={loading}>
                {loading ? 'Refreshing...' : 'Refresh All'}
              </button>
            </div>
          </div>

          {message ? <div className="vpc-alert-success">{message}</div> : null}
          {error ? <div className="vpc-alert-danger">{error}</div> : null}

          <div className="vpc-tabs">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'payments', label: 'Payments' },
              { key: 'settlements', label: 'Settlements' },
              { key: 'issues', label: 'Issues & Alerts' },
              { key: 'reports', label: 'Reports' },
            ].map((tab) => (
              <button
                key={tab.key}
                className={`vpc-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="vpc-card">Loading payment center...</div>
          ) : (
            <>
              {activeTab === 'overview' && renderOverviewTab()}
              {activeTab === 'payments' && renderPaymentsTab()}
              {activeTab === 'settlements' && renderSettlementsTab()}
              {activeTab === 'issues' && renderIssuesTab()}
              {activeTab === 'reports' && renderReportsTab()}
            </>
          )}
        </div>
      </div>

      {selectedPayment && (
        <div className="vpc-modal-backdrop" onClick={() => setSelectedPayment(null)}>
          <div className="vpc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vpc-modal-head">
              <h3 className="vpc-modal-title">Payment Details</h3>
              <button className="vpc-close-btn" onClick={() => setSelectedPayment(null)}>
                ×
              </button>
            </div>

            <div className="vpc-info-grid">
              <div className="vpc-info-item">
                <span className="vpc-label">Order ID</span>
                <span className="vpc-value">
                  {selectedPayment.order?.id || selectedPayment.order_id || '-'}
                </span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Customer</span>
                <span className="vpc-value">
                  {selectedPayment.order?.customer_number || selectedPayment.customer_number || '-'}
                </span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Amount</span>
                <span className="vpc-value">{formatCurrency(selectedPayment.amount)}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Method</span>
                <span className="vpc-value">{selectedPayment.payment_method || '-'}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Payment Status</span>
                <span className="vpc-value">{selectedPayment.payment_status || '-'}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Settlement Status</span>
                <span className="vpc-value">
                  {selectedPayment.settlement?.settlement_status || selectedPayment.settlement_status || '-'}
                </span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Razorpay Order ID</span>
                <span className="vpc-value">{selectedPayment.razorpay_order_id || '-'}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Razorpay Payment ID</span>
                <span className="vpc-value">{selectedPayment.razorpay_payment_id || '-'}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Transfer ID</span>
                <span className="vpc-value">
                  {selectedPayment.settlement?.transfer_id || selectedPayment.razorpay_transfer_id || '-'}
                </span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Created At</span>
                <span className="vpc-value">{formatDateTime(selectedPayment.created_at)}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Paid At</span>
                <span className="vpc-value">{formatDateTime(selectedPayment.paid_at)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedSettlement && (
        <div className="vpc-modal-backdrop" onClick={() => setSelectedSettlement(null)}>
          <div className="vpc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vpc-modal-head">
              <h3 className="vpc-modal-title">Settlement Details</h3>
              <button className="vpc-close-btn" onClick={() => setSelectedSettlement(null)}>
                ×
              </button>
            </div>

            <div className="vpc-info-grid">
              <div className="vpc-info-item">
                <span className="vpc-label">Settlement ID</span>
                <span className="vpc-value">{selectedSettlement.id || selectedSettlement.settlement_id || '-'}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Order ID</span>
                <span className="vpc-value">{selectedSettlement.order_id || '-'}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Payment ID</span>
                <span className="vpc-value">{selectedSettlement.payment_id || '-'}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Gross Amount</span>
                <span className="vpc-value">{formatCurrency(selectedSettlement.gross_amount)}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Commission</span>
                <span className="vpc-value">{formatCurrency(selectedSettlement.commission_amount)}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Net Vendor Amount</span>
                <span className="vpc-value">{formatCurrency(selectedSettlement.net_vendor_amount)}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Transfer ID</span>
                <span className="vpc-value">{selectedSettlement.razorpay_transfer_id || '-'}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Settlement Status</span>
                <span className="vpc-value">{selectedSettlement.settlement_status || '-'}</span>
              </div>
              <div className="vpc-info-item">
                <span className="vpc-label">Settled At</span>
                <span className="vpc-value">{formatDateTime(selectedSettlement.settled_at)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VendorPaymentCenter;
